/* global React, Icon, IconButton, Modal, Button, Switch, ReceiptSinglePaymentSourceDialog */
// ============================================================
// PLUMME 2.0 — App shell (sidebar, topbar, trial banner)
// ============================================================
// Background themes are per-page AND per accounting client (isolated in clientStore).
function readBgMap(client){ try { return (window.clientStore ? window.clientStore.get(client, 'bgByPage', {}) : {}) || {}; } catch(e){ return {}; } }
const BG_THEMES=[
  {key:'default',label:'Plomme',sw:'#A074E0'},
  {key:'mint',label:'Mint',sw:'#5FBFA0'},
  {key:'varm',label:'Varm',sw:'#F0B45E'},
  {key:'sky',label:'Bl\u00e5',sw:'#7BA7EC'},
  {key:'rose',label:'Rose',sw:'#DE8FB4'},
  {key:'skog',label:'Skog',sw:'#5E9E7A'},
];
// Oversatte etiketter for navigasjon. Rute-key er ogs\u00e5 i18n-n\u00f8kkel, slik at
// oppslaget kan gj\u00f8res direkte fra NAV/BOTTOM_NAV uten en egen mapping.
const navLabel=(item)=>t(PlummeI18n.keyFor('nav', item.key, 'route'),item.label);
const navGroupLabel=(group)=>(group.key?t(group.labelKey||('navgroup.'+group.key),group.label):group.label);
// Navigasjonen er ekte lenker. Vanlig klikk hindres og bytter rute i appen (ingen
// full reload), mens hoyreklikk, midtklikk og Ctrl/Cmd-klikk gar til nettleseren —
// det er det som gir "apne i nytt vindu" og flere arbeidsflater samtidig.
// Foretaket henger med i lenka, sa et nytt vindu apner samme foretak — ikke det
// som tilfeldigvis var valgt sist i en annen fane.
const navHref=(routeKey,subPage)=>window.PlummeAppRoute?.urlForRoute?.(routeKey,{
  subPage,
  businessId:window.PlummeBackend?.activeBusinessId||'',
})||undefined;
const navClick=(go)=>(event)=>{
  if(window.PlummeAppRoute?.isModifiedClick?.(event)) return;
  event.preventDefault();
  go();
};
Object.assign(window,{navLabel,navGroupLabel,navHref,navClick});
// Lys/mørk-bryter. Skriver samme nøkkel som Innstillinger (`plumme-dark`) og setter
// data-dark på <html>, som theme.css sine token-overstyringer henger på.
// Persisteres til backend via updateUserPreferencePatch, som slår sammen med
// øvrige preferanser i stedet for å erstatte dem.
function DarkToggle(){
  const [dark,setDark]=useState(()=>{try{return localStorage.getItem('plumme-dark')==='1';}catch(e){return false;}});
  const darkRef=useRef(dark);
  const skipPersist=useRef(true);
  useEffect(()=>{
    darkRef.current=dark;
    if(dark)document.documentElement.dataset.dark='1'; else delete document.documentElement.dataset.dark;
    try{localStorage.setItem('plumme-dark',dark?'1':'0');}catch(e){}
    window.dispatchEvent(new CustomEvent('plumme-dark-changed',{detail:dark}));
    // ikke skriv ved første render, og ikke når endringen kom fra backend
    if(skipPersist.current){skipPersist.current=false;return;}
    if(window.PlummeBackend?.updateUserPreferencePatch){
      window.PlummeBackend.updateUserPreferencePatch({theme:dark?'dark':'light'}).catch(()=>{});
    }
  },[dark]);
  // backend kan sette temaet etter at bryteren har montert — følg med uten å skrive tilbake
  useEffect(()=>{
    const sync=(e)=>{
      const next=!!(e&&e.detail);
      if(next===darkRef.current)return;
      skipPersist.current=true;
      setDark(next);
    };
    window.addEventListener('plumme-dark-changed',sync);
    return ()=>window.removeEventListener('plumme-dark-changed',sync);
  },[]);
  return <button className="topbar-icon-btn" onClick={()=>setDark(d=>!d)}
    data-help-title={t('shell.theme.helpTitle')} data-help={t('shell.theme.help')}
    aria-label={dark?t('shell.theme.toLight'):t('shell.theme.toDark')}
    title={dark?t('shell.theme.light'):t('shell.theme.dark')}><Icon name={dark?'sun':'moon'} size={21}/></button>;
}
// På smale skjermer samles hjelp og tema i én meny; globalt søk følger på de aller smaleste.
// Hvilken variant som vises styres av CSS, ikke av JS — begge er alltid montert.
function ExtrasMenu({ route, client, onOpenSearch }){
  const [open,setOpen]=useState(false);
  return <div className="extras-wrap">
    <button className="topbar-icon-btn extras-btn" onClick={()=>setOpen(o=>!o)} aria-label={t('shell.moreOptions')} title={t('shell.moreOptions')}><Icon name="dots" size={21}/></button>
    {open&&<React.Fragment>
      <div className="topbar-search-scrim" onClick={()=>setOpen(false)}/>
      <div className="card extras-menu">
        <button className="topbar-icon-btn extras-search" onClick={()=>{setOpen(false);onOpenSearch();}} aria-label={t('shell.searchAll')} title={t('shell.searchAll')} data-help-title={t('shell.searchAll')} data-help={t('shell.searchHelp')}><Icon name="search" size={21}/></button>
        {window.HelpButton && <window.HelpButton className="topbar-icon-btn"/>}
        <DarkToggle/>
        <BgThemePicker route={route} client={client}/>
      </div>
    </React.Fragment>}
  </div>;
}
function BgThemePicker({ route, client }){
  const [open,setOpen]=useState(false);
  const [map,setMap]=useState(()=>readBgMap(client));
  useEffect(()=>{ setMap(readBgMap(client)); },[client]);
  const bg=map[route]||'default';
  useEffect(()=>{
    if(bg&&bg!=='default')document.documentElement.dataset.bg=bg; else delete document.documentElement.dataset.bg;
  },[bg]);
  // Velgeren rendres to ganger (topplinje + ExtrasMenu). Bare én er synlig av gangen,
  // men begge holder eget map — uten denne synkroniseringen ville den skjulte hatt
  // utdatert state og kunne satt feil bakgrunn ved neste ruteskifte.
  useEffect(()=>{
    const sync=()=>setMap(readBgMap(client));
    window.addEventListener('plumme-bg-changed',sync);
    return ()=>window.removeEventListener('plumme-bg-changed',sync);
  },[client]);
  const pick=(key)=>{const next={...map,[route]:key};setMap(next);try{ if(window.clientStore) window.clientStore.set(client,'bgByPage',next); }catch(e){}window.dispatchEvent(new Event('plumme-bg-changed'));setOpen(false);};
  return <div className="year-wrap">
    <button className="topbar-icon-btn" onClick={()=>setOpen(o=>!o)} aria-label={t('shell.bg.label')} title={t('shell.bg.title')} data-help-title={t('shell.bg.label')} data-help={t('shell.bg.help')}><Icon name="palette" size={22}/></button>
    {open&&<React.Fragment>
      <div className="topbar-search-scrim" onClick={()=>setOpen(false)}/>
      <div className="card bg-theme-menu">
        <div className="bg-theme-title">{t('shell.bg.menuTitle')}</div>
        {/* NB: iterasjonsvariabelen kan ikke hete `t` her — den ville skygge for den globale t(). */}
        <div className="bg-theme-grid">{BG_THEMES.map(theme=>
          <button key={theme.key} className={'bg-theme-opt'+(bg===theme.key?' active':'')} onClick={()=>pick(theme.key)}>
            <span className="bg-theme-sw" style={{background:theme.sw}}/>{t('shell.bg.'+theme.key,theme.label)}</button>)}</div>
        {window.SkinPicker&&<window.SkinPicker/>}
      </div>
    </React.Fragment>}
  </div>;
}
// Uleste support-svar: telleren speiles av en Cloud Functions-trigger inn i et
// lite per-foretak dokument (`businesses/{id}/supportTicketStatus/summary`)
// klienten faar lese — raa ticket-data er fortsatt lukket for klient-SDK-er,
// se docs/features/customer-support.md. Live `onSnapshot` gir gjeldende
// verdi med en gang ved abonnering og pusher hver endring, sa det trengs
// ingen poll/focus/visibilitychange-fallback lenger.
function useSupportUnreadCount() {
  const [count, setCount] = useState(0);
  const businessId = window.PlummeBackend?.activeBusinessId || '';
  useEffect(() => {
    if (!window.PlummeBackend?.subscribeSupportUnreadCount || !businessId) { setCount(0); return undefined; }
    return window.PlummeBackend.subscribeSupportUnreadCount({ businessId }, setCount);
  }, [businessId]);
  return count;
}
const NAV = [
{ items: [{ key: 'hjem', label: 'Hjem', icon: 'home' }] },
{ key: 'ai', label: 'AI', labelKey: 'navgroup.ai', items: [
  { key: 'superplumma', label: 'SuperPlumma', icon: 'sparkle' },
  { key: 'aiautopilot', label: 'AI Regnskapsfører', icon: 'sparkle', featured: true },
  { key: 'aiinnsikt', label: 'AI Rådgiver', icon: 'trendUp' },
] },
{ key: 'salg', label: 'Salg', labelKey: 'navgroup.sales', items: [
  { key: 'faktura', label: 'Nytt salg', icon: 'fileText' },
  { key: 'fakturaliste', label: 'Fakturaliste', icon: 'list' },
  { key: 'dagsoppgjor', label: 'Dagsoppgjør', icon: 'receipt' },
  { key: 'kunder', label: 'Kunder', icon: 'users' },
  { key: 'produkter', label: 'Produkter', icon: 'box' },
  { key: 'prosjekter', label: 'Prosjekter', icon: 'clock' },
  { key: 'abonnement', label: 'Avtaler', icon: 'refresh' }]
},
{ key: 'kjop', label: 'Kjøp', labelKey: 'navgroup.purchases', items: [
  { key: 'nyttkjop', label: 'Nytt kjøp', icon: 'bag' },
  { key: 'kjopsoversikt', label: 'Kjøpsoversikt', icon: 'receipt' },
  { key: 'fripostering', label: 'Fri postering', icon: 'files' },
  { key: 'leverandorer', label: 'Leverandører', icon: 'building' },
  { key: 'eiendeler', label: 'Eiendeler', icon: 'briefcase' }, { key: 'investeringer', label: 'Investeringer', icon: 'trendUp' },
  { key: 'varelager', label: 'Varelager', icon: 'box' },
  { key: 'innboks', label: 'Innboks', icon: 'mail', featured: true }]
},
{ key: 'rapporter', label: 'Rapporter', labelKey: 'navgroup.reports', items: [
  { key: 'resultatregnskap', label: 'Resultatregnskap', icon: 'trendUp' },
  { key: 'saldobalanse', label: 'Saldobalanse', icon: 'sliders' },
  { key: 'budsjett', label: 'Budsjett', icon: 'pie' }]
},
{ key: 'innleveringer', label: 'Innleveringer', labelKey: 'navgroup.filings', items: [
  { key: 'innsendinger', label: 'MVA-melding', icon: 'percent' },
  { key: 'skattemelding', label: 'Skattemelding', icon: 'doc' },
  { key: 'arsavslutning', label: 'Årsavslutning', icon: 'checkCircle' },
  { key: 'arsregnskap', label: 'Årsregnskap', icon: 'files' }]
},
{ key: 'ansatte', label: 'Ansatte', labelKey: 'navgroup.employees', items: [
  { key: 'lonn', label: 'Lønn', icon: 'wallet' },
  { key: 'kjorebok', label: 'Kjørebok', icon: 'car' }]
},
{ key: 'annet', label: 'Annet', labelKey: 'navgroup.other', items: [
  { key: 'allebilag', label: 'Transaksjoner', icon: 'receipt' },
  { key: 'bank', label: 'Bank', icon: 'bank', featured: true }]
},
{ key: 'ovrig', label: 'Øvrig', labelKey: 'navgroup.miscellaneous', items: [
  { key: 'minside', label: 'Min side', icon: 'users' },
  { key: 'aktivitetslogg', label: 'Aktivitetslogg', icon: 'list' },
  { key: 'support', label: 'Support', icon: 'chat' },
  { key: 'innstillinger', label: 'Innstillinger', icon: 'settings' }]
}];
// Hjelpemodus-tekster for navigasjonen (per rute-key).
const NAV_HELP = {
  hjem: 'Startsiden din – nøkkeltall, grafer og snarveier til å registrere salg og kjøp.',
  aiautopilot: 'SuperPlumma går gjennom bank, bilag, MVA og frister og lager forslag. Du kontrollerer og godkjenner før noe bokføres.',
  aiinnsikt: 'Økonomisk rådgivning fra SuperPlumma – helsescore, anbefalinger, kontantstrømprognose, risiko og skatteoptimalisering.',
  faktura: 'Lag en ny faktura eller et tilbud til en kunde. Salget bokføres automatisk.',
  fakturaliste: 'Oversikt over alle fakturaer og tilbud – status, forfall og betaling.',
  dagsoppgjor: 'Dagsoppgjør fra kassesystemer, bookingsystemer, utleieplattformer og nettbutikker. Kontroller og godkjenn før bokføring.',
  kunder: 'Kunderegisteret ditt. Legg til og rediger kunder du fakturerer.',
  produkter: 'Varer og tjenester du selger, med pris og MVA-sats – klare til bruk på faktura.',
  prosjekter: 'Før timer på prosjekter og aktiviteter, følg med på hva som er gjort, og gjør ufakturerte timer om til fakturautkast.',
  abonnement: 'Abonnement, repeterende fakturaer og kundelister – med samlet kontroll over automatiske utsendinger.',
  nyttkjop: 'Registrer et kjøp eller en leverandørfaktura med bilag, leverandør, beløp og konto.',
  fripostering: 'Bokfør manuelt rett på konto når du trenger full kontroll over føringen.',
  leverandorer: 'Leverandørregisteret ditt – navn, org.nr. og kontonummer for utbetalinger.',
  eiendeler: 'Driftsmidler og varige verdier som avskrives over tid.',
  varelager: 'Varetelling og verdien av varelageret ditt. Dokumenter tellingen og bokfør endringen i beholdningen.',
  innboks: 'Bilag som er kommet inn, men ikke bokført ennå. Dra dem på riktig føring.',
  resultatregnskap: 'Inntekter minus kostnader – ser du om bedriften går med overskudd.',
  saldobalanse: 'Saldo på alle konti – grunnlaget for regnskapsrapportene dine.',
  budsjett: 'Sett opp budsjett og følg med på hvordan du ligger an mot målene.',
  innsendinger: 'Kontroller MVA-grunnlaget for hver termin. Selve meldingen leveres hos Skatteetaten.',
  skattemelding: 'Utkast til skattemelding basert på regnskapet – kontroller tallene før du leverer hos Skatteetaten.',
  arsavslutning: 'Steg-for-steg gjennom alt som må gjøres for å lukke regnskapsåret.',
  arsregnskap: 'Resultat, balanse, noter og signering. Årsregnskapet leveres til Regnskapsregisteret utenfor Plumme.',
  lonn: 'Kjør lønn, lag lønnsslipper og bokfør lønnen. A-meldingen leveres utenfor Plumme.',
  kjorebok: 'Registrer kjøring i jobben og se kilometer og fradrag for året.',
  allebilag: 'Samlet oversikt over bokførte transaksjoner og tilhørende bilag – søk og finn igjen.',
  bank: 'Last opp kontoutskrifter og avstem bankkontoene mot regnskapet.',
  minside: 'Din egen profil og innstillinger som bruker.',
  aktivitetslogg: 'Full historikk over hva som er gjort i regnskapet, og av hvem.',
  support: 'Se og følg opp support-saker du har sendt inn.',
  innstillinger: 'Innstillinger for selskapet, regnskapet og appen.',
};

// NAV_HELP over er norsk kilde og fallback; oversettelsen ligger på navhelp.<rute>.
const navHelp = (item) => t(PlummeI18n.keyFor('navhelp', item.key, 'route'), NAV_HELP[item.key] || t('navhelp.fallback', { label: navLabel(item) }));

const PAGE_LOCKED = { hjem: true, support: true, innstillinger: true, minside: true };
const PAGE_RULE = {}, MENU_PREFERENCES = window.PlummeMenuPreferences;
const HIDDEN_PAGES_KEY = 'plumme-hidden-pages';
function getHiddenPages() {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_PAGES_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}
function setHiddenPages(next) {
  try {
    localStorage.setItem(HIDDEN_PAGES_KEY, JSON.stringify(next || {}));
  } catch (e) {}
  window.dispatchEvent(new Event('plumme-pages-changed'));
}
Object.assign(window, { PLUMME_NAV: NAV, PAGE_LOCKED, PAGE_RULE, getHiddenPages, setHiddenPages });
const CLIENTS = [];
const EMPLOYEE_PORTAL_SELECTION_KEY = 'plumme-selected-employee-portal-key';
function getClients() {
  const memberships = window.PlummeBackend?.memberships || [];
  if (memberships.length) {
    return memberships.map((m) => ({
      id: m.businessId,
      name: m.businessName || m.businessId,
      org: m.organizationNumber || '',
      clientType: m.clientType || (m.privateEconomy ? 'personal' : 'business'),
      privateEconomy: m.privateEconomy === true || m.clientType === 'personal',
      role: m.role,
    }));
  }
  return [];
}
function clientInitials(name) { return String(name || '?').replace(/\s*AS$/i, '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?'; }
function clientSubtitle(client) { return client?.privateEconomy ? t('shell.client.personalEconomy') : client?.org ? t('shell.client.org', { org: client.org }) : t('shell.client.accountingClient'); }
function clientIconName(client) { return client?.privateEconomy ? 'wallet' : 'building'; }
function employeePortalKey(portal) { return `${portal?.businessId || ''}-${portal?.employee?.id || ''}`; }
function rememberEmployeePortal(portalOrKey) {
  const key = typeof portalOrKey === 'string' ? portalOrKey : employeePortalKey(portalOrKey);
  if (!key || key === '-') return;
  window.__plummeSelectedEmployeePortalKey = key;
  try { localStorage.setItem(EMPLOYEE_PORTAL_SELECTION_KEY, key); } catch (e) {}
}
function getRememberedEmployeePortal() {
  try { return window.__plummeSelectedEmployeePortalKey || localStorage.getItem(EMPLOYEE_PORTAL_SELECTION_KEY) || ''; } catch (e) { return window.__plummeSelectedEmployeePortalKey || ''; }
}
function useEmployeePortalSwitcherRows(userId) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!userId || !window.PlummeBackend?.getEmployeePortals) {
      setRows([]);
      return () => { cancelled = true; };
    }
    window.PlummeBackend.getEmployeePortals()
      .then((portals) => { if (!cancelled) setRows(Array.isArray(portals) ? portals : []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [userId]);
  return rows;
}

// Logg ut nederst i klientvelgeren. Egen overskrift og skillelinje gjør at den
// ikke ligger klistret inntil siste klient — knappen er destruktiv nok til at et
// feiltrykk koster, særlig med finger.
function SignOutMenuSection({ setRoute, onDone }) {
  const signOutNow = async () => {
    onDone && onDone();
    try { await window.PlummeBackend?.signOut?.(); } catch (error) { toast?.(error?.message || t('shell.client.signOutFailed'), 'error'); return; }
    setRoute && setRoute('logginn');
  };
  return <React.Fragment>
    <div className="sb-client-menu-h sb-client-menu-sep">{t('common.signOut')}</div>
    <button className="sb-client-item sb-logout-item" onClick={signOutNow}>
      <span className="sb-logout-ico"><Icon name="logout" size={16} /></span>
      <span className="sb-client-meta"><span className="sb-client-name">{t('common.signOut')}</span></span>
    </button>
  </React.Fragment>;
}

function ClientSwitcher({ client, setClient, setRoute, employeePortals = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const clients = getClients();
  const privateClients = clients.filter((c) => c.privateEconomy);
  const businessClients = clients.filter((c) => !c.privateEconomy);
  const ownedClients = businessClients.filter((c) => c.role === 'owner');
  const assignedClients = businessClients.filter((c) => c.role !== 'owner');
  const cur = clients.find((c) => c.id === client) || clients[0] || { id: '', name: t('shell.client.none'), org: '' };
  const openEmployer = (portal) => {
    rememberEmployeePortal(portal);
    setRoute && setRoute('ansattportal');
    setOpen(false);
  };
  const openClient = (selectedClient) => {
    const next = typeof selectedClient === 'object' ? selectedClient : clients.find((c) => c.id === selectedClient) || { id: selectedClient };
    setClient(next.id);
    setRoute && setRoute(next.privateEconomy ? 'privatokonomi' : 'hjem');
    setOpen(false);
  };
  return <div className="sb-client" ref={ref}>
    <button className="sb-client-btn" onClick={() => setOpen((o) => !o)} title={cur.name}>
      <span className="sb-client-ava">{clientInitials(cur.name)}</span>
      <span className="sb-client-meta">
        <span className="sb-client-name">{cur.name}</span>
      </span>
      <Icon name="chevDown" size={16} />
    </button>
    {open && <div className="sb-client-menu">
      {employeePortals.length ? <React.Fragment>
        <div className="sb-client-menu-h">{t('shell.client.employers')}</div>
        {employeePortals.map((portal) => {
          const key = employeePortalKey(portal);
          return <button key={key} className="sb-client-item sb-emp-item" onClick={() => openEmployer(portal)}>
            <span className="sb-client-ava sb-emp-ava sm">{clientInitials(portal.businessName)}</span>
            <span className="sb-client-meta"><span className="sb-client-name">{portal.businessName || t('shell.client.employer')}</span><span className="sb-client-sub">{portal.employee?.jobTitle || portal.employee?.name || t('shell.client.employee')}</span></span>
            {getRememberedEmployeePortal() === key && <Icon name="check" size={16} />}
          </button>;
        })}
      </React.Fragment> : null}
      {privateClients.length ? <React.Fragment>
        <div className="sb-client-menu-h">{t('shell.client.private')}</div>
        {privateClients.map((c) =>
          <button key={c.id} className={'sb-client-item sb-private-item' + (c.id === cur.id ? ' active' : '')} onClick={() => openClient(c)}>
            <span className="sb-client-ava sm sb-private-ava"><Icon name={clientIconName(c)} size={15} /></span>
            <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{clientSubtitle(c)}</span></span>
            {c.id === cur.id && <Icon name="check" size={16} />}
          </button>)}
      </React.Fragment> : null}
      {ownedClients.length ? <React.Fragment><div className="sb-client-menu-h">{t('shm.myBusinesses')}</div>{ownedClients.map((c) =>
        <button key={c.id} className={'sb-client-item' + (c.id === cur.id ? ' active' : '') + (c.privateEconomy ? ' sb-private-item' : '')} onClick={() => openClient(c)}>
          <span className={'sb-client-ava sm' + (c.privateEconomy ? ' sb-private-ava' : '')}>{c.privateEconomy ? <Icon name={clientIconName(c)} size={15} /> : clientInitials(c.name)}</span>
          <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{clientSubtitle(c)}</span></span>
          {c.id === cur.id && <Icon name="check" size={16} />}
        </button>)}</React.Fragment> : null}
      {assignedClients.length ? <React.Fragment><div className="sb-client-menu-h">{t('shm.clientAssignments')}</div>{assignedClients.map((c) =>
        <button key={c.id} className={'sb-client-item' + (c.id === cur.id ? ' active' : '')} onClick={() => openClient(c)}>
          <span className="sb-client-ava sm">{clientInitials(c.name)}</span>
          <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{c.role === 'accountant' ? t('set.role.accountant') : c.role === 'admin' ? t('rf.role.admin') : t('set.role.viewer')}</span></span>
          {c.id === cur.id && <Icon name="check" size={16} />}
        </button>)}</React.Fragment> : null}
      {!businessClients.length && !privateClients.length ? <div className="sb-client-item"><span className="sb-client-meta"><span className="sb-client-name">{t('shell.client.noBusinesses')}</span><span className="sb-client-sub">{t('shell.client.connecting')}</span></span></div> : null}
      {/* Byraaflaten er et eget skall, ikke et foretak. Den vises bare for den som
          faktisk er medlem av et regnskapsbyraa. */}
      {(window.PlummeBackend?.firmMemberships || []).length ? <React.Fragment>
        <div className="sb-client-menu-h">{t('shell.client.firm', 'Regnskapsbyrå')}</div>
        <button className="sb-client-item" onClick={() => { setOpen(false); setRoute && setRoute('regnskapsforer'); }}>
          <span className="sb-client-ava sm"><Icon name="building" size={15} /></span>
          <span className="sb-client-meta">
            <span className="sb-client-name">{(window.PlummeBackend.firmMemberships[0] || {}).firmName || t('shell.client.firm', 'Regnskapsbyrå')}</span>
            <span className="sb-client-sub">{t('shell.client.firmSub', 'Klientoversikt og avstemming')}</span>
          </span>
        </button>
      </React.Fragment> : null}
      <SignOutMenuSection setRoute={setRoute} onDone={() => setOpen(false)} />
    </div>}
  </div>;
}

const PLUM_MARK = <svg className="mark" viewBox="0 0 100 100"><defs><radialGradient id="bm" cx="38%" cy="32%" r="78%"><stop offset="0%" stopColor="#9B5FD6" /><stop offset="60%" stopColor="#6D28B5" /><stop offset="100%" stopColor="#4A1772" /></radialGradient></defs><ellipse cx="50" cy="56" rx="36" ry="35" fill="url(#bm)" /><ellipse cx="38" cy="42" rx="11" ry="8" fill="#fff" opacity=".2" /><path d="M50 24 C50 16 51 12 53 8" stroke="#5A3A1A" strokeWidth="4" fill="none" strokeLinecap="round" /><path d="M53 12 C63 6 78 8 82 17 C73 25 58 23 53 14 Z" fill="#46B36B" /></svg>;
// Tilpass sidemenyen. Skjulte sider ligger i brukerprofilens preferanser
// (samme kilde som Innstillinger → Sidemeny), med localStorage som cache.
function persistHiddenPages(next) {
  setHiddenPages(next);
  try {
    if (window.PlummeBackend?.updateUserPreferencePatch) {
      window.PlummeBackend.updateUserPreferencePatch({ hiddenPages: next }).catch(() => {});
    }
    if (window.PlummeBackend?.updateState) {
      window.PlummeBackend.updateState((current) => ({
        ...current,
        settings: { ...(current.settings || {}), uiPreferences: { ...((current.settings || {}).uiPreferences || {}), hiddenPages: next } },
      }), true);
    }
  } catch (e) {}
}
function PageCustomizer({ open, onClose, aa = true, vatRegistered = true }) {
  const [hidden, setHidden] = useState(getHiddenPages);
  useEffect(() => { if (open) setHidden(getHiddenPages()); }, [open]);
  if (!open) return null;
  const hideable = (it) => !PAGE_LOCKED[it.key] && !PAGE_RULE[it.key];
  const apply = (next) => { setHidden(next); persistHiddenPages(next); };
  const toggle = (key) => { const next = { ...hidden }; if (next[key]) delete next[key]; else next[key] = true; apply(next); };
  const total = NAV.reduce((n, g) => n + g.items.length, 0);
  const visible = NAV.reduce((n, g) => n + g.items.filter((it) => !hidden[it.key]).length, 0);
  const allOn = visible === total;
  const aiModeOn = MENU_PREFERENCES.matchesMode(NAV, hidden, PAGE_LOCKED, PAGE_RULE, MENU_PREFERENCES.AI_MODE_PAGE_KEYS);
  const setAll = (on) => {
    const next = {};
    if (!on) NAV.forEach((g) => g.items.forEach((it) => { if (hideable(it)) next[it.key] = true; }));
    apply(next);
  };
  // portal: dialogen rendres inne i .sidebar, som har backdrop-filter og derfor
  // blir containing block for position:fixed — uten portal havner overlayet
  // inni sidemenyen i stedet for midt på skjermen.
  return <Modal open portal onClose={onClose} title={t('shell.customizeTitle')} max={560}
    titleRight={<span className="t-sm muted" style={{ fontWeight: 700 }}>{t('shell.customizeVisible', { visible, total })}</span>}
    footer={<Button onClick={onClose}>{t('shell.customizeDone')}</Button>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <div className="t-sm muted" style={{ fontWeight: 600 }}>{t('shell.customizeLead')}</div>
      <div className="pc-row pc-row-all">
        <span className="pc-ico" style={{ background: 'var(--plum-600)', color: '#fff' }}><Icon name="list" size={18} /></span>
        <span className="pc-label">{t('shell.customizeAllPages')}</span>
        <Switch on={allOn} onChange={() => setAll(!allOn)} />
      </div>
      <div className="pc-row pc-row-ai"><span className="pc-ico"><Icon name="sparkle" size={18} /></span><span className="pc-label">{t('shell.customizeAiMode')}</span><Switch on={aiModeOn} onChange={() => apply(aiModeOn ? {} : MENU_PREFERENCES.hiddenPagesForMode(NAV, PAGE_LOCKED, PAGE_RULE, MENU_PREFERENCES.AI_MODE_PAGE_KEYS))} /></div>
      {NAV.map((g, gi) => <div key={gi} className="pc-group">
        {g.label && <div className="pc-group-label">{navGroupLabel(g)}</div>}
        {g.items.filter((it) => (aa || it.key !== 'arsregnskap') && (vatRegistered || it.key !== 'innsendinger') && !PAGE_RULE[it.key]).map((it) => <div key={it.key} className="pc-row">
          <span className="pc-ico"><Icon name={it.icon} size={18} /></span>
          <span className="pc-label">{navLabel(it)}</span>
          {PAGE_LOCKED[it.key] ? <span className="pc-locked">{t('shell.customizeAlwaysOn')}</span> : <Switch on={!hidden[it.key]} onChange={() => toggle(it.key)} />}
        </div>)}
      </div>)}
    </div>
  </Modal>;
}

function Sidebar({ route, setRoute, collapsed, setCollapsed, mobileOpen, closeMobile, client, setClient, lonnPage, setLonnPage, transactionPage, setTransactionPage, supportPage, setSupportPage, employeePortals = [], aa = true, vatRegistered = true }) {
  const [hiddenPages,setHiddenPagesState]=useState(()=>getHiddenPages());
  const [pcOpen,setPcOpen]=useState(false);
  const supportUnread=useSupportUnreadCount();
  // Tablet: ikon-railen kan utvides midlertidig for å lese etikettene
  const [peek,setPeek]=useState(false);
  useEffect(()=>{ setPeek(false); },[route]);
  useEffect(()=>{
    const sync=()=>setHiddenPagesState(getHiddenPages());
    window.addEventListener('plumme-pages-changed',sync);
    return ()=>window.removeEventListener('plumme-pages-changed',sync);
  },[]);
  const isVisible=(item)=>(item.key!=='arsregnskap'||aa)&&(item.key!=='innsendinger'||vatRegistered)&&(!hiddenPages[item.key]||PAGE_LOCKED[item.key]||PAGE_RULE[item.key]), subPagesFor=(key)=>key==='lonn'&&route==='lonn'?(window.PAY_PAGES?.()||[]):key==='allebilag'&&['allebilag','transaksjon'].includes(route)?window.TRANSACTION_PAGES:key==='support'&&route==='support'?(window.SUPPORT_PAGES?.()||null):null, selectedSubPage=(key)=>key==='lonn'?lonnPage:key==='support'?supportPage:transactionPage, selectSubPage=(key,value)=>{if(key==='lonn')setLonnPage?.(value);else if(key==='support')setSupportPage?.(value);else setTransactionPage?.(value);setRoute(key);closeMobile?.();};
  return <aside className={'sidebar' + (collapsed ? ' collapsed' : '') + (mobileOpen ? ' mobile-open' : '') + (peek ? ' peek' : '')}>
    <div className="sb-brand">
      <div className="row between">
        <div className="sb-logo"><span className="sb-logo-text">Plumme</span></div>
        <button className="sb-peek-toggle" onClick={() => setPeek((p) => !p)} aria-label={peek ? t('shell.hideLabels') : t('shell.showLabels')} title={peek ? t('shell.hideLabels') : t('shell.showLabels')}><Icon name={peek ? 'x' : 'menu'} size={18} /></button>
        <button className="sb-mobile-close" onClick={closeMobile} aria-label={t('shell.closeMenu')}><Icon name="x" size={20} /></button>
      </div>
      <ClientSwitcher client={client} setClient={setClient} setRoute={setRoute} employeePortals={employeePortals} />
    </div>
    <nav className="sb-nav">
      {NAV.map((g, gi) => {
        const visibleItems=g.items.filter(isVisible);
        if(!visibleItems.length)return null;
        return <div key={gi}>
        {g.label && <div className="sb-group-label">{navGroupLabel(g)}</div>}
        {visibleItems.map((it) =>
        <React.Fragment key={it.key}>
          <a className={'sb-item' + (route===it.key||(route==='transaksjon'&&it.key==='allebilag') ? ' active' : '') + (it.featured ? ' featured' : '')} href={navHref(it.key)}
            onClick={navClick(() => { setRoute(it.key); closeMobile && closeMobile(); })} title={navLabel(it)} data-help-title={navLabel(it)} data-help={navHelp(it)}>
            <span className="sb-nav-ico"><Icon name={it.icon} size={20} />{it.key==='support'&&supportUnread>0&&<Badge variant="error" dot/>}</span><span className="sb-label">{navLabel(it)}</span>
          </a>
          {!collapsed && subPagesFor(it.key) &&
            <div className="sb-sublist">
              {subPagesFor(it.key).map((p) => <React.Fragment key={p.value}>
                {p.sep && <div className="sb-subsep" />}
                <a className={'sb-subitem' + (selectedSubPage(it.key) === p.value ? ' active' : '')} href={navHref(it.key, p.value)}
                  onClick={navClick(() => selectSubPage(it.key,p.value))}>{p.labelKey?t(p.labelKey,p.label):p.label}</a>
              </React.Fragment>)}
            </div>}
        </React.Fragment>)}
      </div>;})}
    </nav>
    <div className="sb-collapse">
      <button onClick={() => setPcOpen(true)}
        data-help-title={t('shell.customizeHelpTitle')} data-help={t('shell.customizeHelp')}>
        <Icon name="settings" size={18} /><span className="sb-label">{t('shell.customizeMenu')}</span>
      </button>
      <button onClick={() => setCollapsed((c) => !c)}>
        <Icon name={collapsed ? 'chevRight' : 'chevLeft'} size={18} /><span className="sb-label">{t('shell.collapseMenu')}</span>
      </button>

    </div>
    <div className="sb-cam-row"><CamCaptureButton setRoute={setRoute} align="right" /></div>
    <PageCustomizer open={pcOpen} onClose={() => setPcOpen(false)} aa={aa} vatRegistered={vatRegistered} />
    {peek && <div className="sb-peek-scrim" onClick={() => setPeek(false)} />}
  </aside>;
}

function availableAccountingYears(activeYear) {
  const state = window.PlummeBackend?.appState || {};
  const now = new Date().getFullYear();
  const years = new Set([now, now - 1, now - 2, Number(activeYear)]);
  const addYear = (value) => {
    const year = Number(value);
    if (Number.isInteger(year) && year >= 2000 && year <= now + 1) years.add(year);
  };
  let startYear = Number(state.settings?.startYear);
  if (!Number.isInteger(startYear) || startYear < 2000 || startYear > now + 1) startYear = 0;
  if (startYear) for (let year = Math.max(now, startYear); year >= startYear; year -= 1) addYear(year);
  Object.keys(state.openingBalanceByYear || {}).forEach(addYear);
  for (const year of years) if (year < startYear) years.delete(year);
  Object.keys(state.accountingImportReportStatsByYear || {}).forEach(addYear);
  const imported = state.accountingImportArchiveSummary?.importYears; if (Array.isArray(imported)) imported.forEach(addYear);
  return [...years].filter((year) => Number.isInteger(year) && year >= 2000).sort((a, b) => b - a);
}

function Topbar({ year, setYear, onMenu, clientName, route, client, setClient, setRoute }) {
  const [open, setOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchQuery(''); setSearchOpen(true); } };
    const onOpen = (e) => { setSearchQuery((e && e.detail) || ''); setSearchOpen(true); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('plumme-open-search', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('plumme-open-search', onOpen); };
  }, []);
  const topbarClients = getClients();
  const topbarPrivateClients = topbarClients.filter((c) => c.privateEconomy);
  const topbarBusinessClients = topbarClients.filter((c) => !c.privateEconomy);
  const topbarOwnedClients = topbarBusinessClients.filter((c) => c.role === 'owner');
  const topbarAssignedClients = topbarBusinessClients.filter((c) => c.role !== 'owner');
  const yearOptions = availableAccountingYears(year);
  const openTopbarClient = (c) => {
    setClient && setClient(c.id);
    setRoute && setRoute(c.privateEconomy ? 'privatokonomi' : 'hjem');
    setClientOpen(false);
  };
  return <div className="topbar">
    <button className="topbar-menu" onClick={onMenu} aria-label={t('shell.openMenu')}><Icon name="list" size={22} /></button>
    <div className="topbar-brand" aria-hidden="true">{PLUM_MARK}<span className="topbar-brand-text">Plumme</span></div>
    <div className="company-wrap grow">
      <button className="company-name" onClick={() => setClientOpen((o) => !o)} data-help-title={t('shell.companyHelpTitle')} data-help={t('shell.companyHelp')}>{clientName || COMPANY.name}<Icon name="chevDown" size={18} className="company-caret" /></button>
      {clientOpen && <React.Fragment>
        <div className="pop-scrim" onClick={() => setClientOpen(false)} />
        <div className="company-menu">
          {topbarPrivateClients.length ? <React.Fragment>
            <div className="sb-client-menu-h">{t('shell.client.private')}</div>
            {topbarPrivateClients.map((c) => <button key={c.id} className={'sb-client-item sb-private-item' + (c.id === client ? ' active' : '')} onClick={() => openTopbarClient(c)}>
              <span className="sb-client-ava sm sb-private-ava"><Icon name={clientIconName(c)} size={15} /></span>
              <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{clientSubtitle(c)}</span></span>
              {c.id === client && <Icon name="check" size={16} />}
            </button>)}
          </React.Fragment> : null}
          {topbarOwnedClients.length ? <div className="sb-client-menu-h">{t('shm.myBusinesses')}</div> : null}
          {topbarOwnedClients.map((c) => <button key={c.id} className={'sb-client-item' + (c.id === client ? ' active' : '')} onClick={() => openTopbarClient(c)}>
            <span className={'sb-client-ava sm' + (c.privateEconomy ? ' sb-private-ava' : '')}>{c.privateEconomy ? <Icon name={clientIconName(c)} size={15} /> : clientInitials(c.name)}</span>
            <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{clientSubtitle(c)}</span></span>
            {c.id === client && <Icon name="check" size={16} />}
          </button>)}
          {topbarAssignedClients.length ? <div className="sb-client-menu-h">{t('shm.clientAssignments')}</div> : null}
          {topbarAssignedClients.map((c) => <button key={c.id} className={'sb-client-item' + (c.id === client ? ' active' : '')} onClick={() => openTopbarClient(c)}>
            <span className="sb-client-ava sm">{clientInitials(c.name)}</span>
            <span className="sb-client-meta"><span className="sb-client-name">{c.name}</span><span className="sb-client-sub">{c.role === 'accountant' ? t('set.role.accountant') : c.role === 'admin' ? t('rf.role.admin') : t('set.role.viewer')}</span></span>
            {c.id === client && <Icon name="check" size={16} />}
          </button>)}
          <SignOutMenuSection setRoute={setRoute} onDone={() => setClientOpen(false)} />
        </div>
      </React.Fragment>}
    </div>
    <div data-app-control="true" className="search-pill topbar-search" data-help-title={t('shell.searchAll')} data-help={t('shell.searchHelp')} onClick={() => { setSearchQuery(''); setSearchOpen(true); }}>
      <Icon name="search" size={19} /><input readOnly placeholder={t('shell.searchAll')} style={{ cursor: 'pointer' }} onFocus={() => { setSearchQuery(''); setSearchOpen(true); }} />
    </div>
    <div className="topbar-extras-desktop">
      {window.HelpButton && <window.HelpButton className="topbar-icon-btn" />}
      <DarkToggle />
      <BgThemePicker route={route} client={client} />
    </div>
    <ExtrasMenu route={route} client={client} onOpenSearch={() => { setSearchQuery(''); setSearchOpen(true); }} />
    <div className="year-wrap">
      <div data-app-control="true" className="year-pill" onClick={() => setOpen((o) => !o)} data-help-title={t('shell.yearHelpTitle')} data-help={t('shell.yearHelp')}>
        {year}<span className="yb"><Icon name="chevDown" size={16} /></span>
      </div>
      {open && <div className="card year-menu">
        {yearOptions.map((y) => <button key={y} className="sb-item" style={{ margin: 0 }} onClick={() => {setYear(y);setOpen(false);}}>{y}</button>)}
      </div>}
    </div>
    {searchOpen && (window.SuperSearch
      ? <window.SuperSearch initialQuery={searchQuery} onClose={() => setSearchOpen(false)} />
      : <React.Fragment>
          <div className="topbar-search-scrim" onClick={() => setSearchOpen(false)} />
          <div className="topbar-search-pop">
            <div className="search-pill" style={{ flex: 1 }}>
              <Icon name="search" size={19} /><input autoFocus placeholder={t('shell.searchPlaceholderLong')} />
            </div>
            <IconButton name="x" variant="ghost" onClick={() => setSearchOpen(false)} />
          </div>
        </React.Fragment>)}
  </div>;
}

function TrialBanner({ onClose, onUpgrade, info }) {
  const daysLeft = info?.daysLeft;
  const daysText = daysLeft == null
    ? t('trial.active')
    : t(daysLeft === 1 ? 'trial.dayLeft' : 'trial.daysLeft', { days: daysLeft });
  const progress = info?.progress == null ? 40 : info.progress;
  return <div className="trial">
    <div className="grow">
      <div className="t-h3">{t('trial.title')}</div>
      <div className="t-sm muted"><b style={{ color: 'var(--plum-700)' }}>{daysText}</b> {t('trial.body')}</div>
    </div>
    <button className="btn btn-secondary btn-sm" onClick={onUpgrade}>{t('trial.upgrade')}</button>
    <IconButton name="x" size="sm" variant="ghost" onClick={onClose} />
    <span className="tprog" style={{ width: `${progress}%` }} />
  </div>;
}

// `labelKey` peker på i18n-nøkkelen; `label` blir stående som norsk fallback.
const bottomLabel = (entry) => t(entry.labelKey || '', entry.label);
const BOTTOM_NAV = window.PlummeMenuPreferences.BOTTOM_NAV;
// Kameraknapp for bilag: sidemenyrad på desktop, flytende over bunnmenyen ≤610px.
// Åpner kamera/filvelger, laster bilaget opp til Innboks og analyserer det —
// samme flyt som den tidligere hjørneknappen på Hjem.
function CamCapturePop({ onPick, onClose, align, busy, showProject }) {
  return <React.Fragment>
    <div className="pop-scrim" onClick={onClose} />
    <div className={'cam-pop' + (align ? ' ' + align : '')}>
      {/* «Til prosjekt» vises bare for foretak som faktisk har et aktivt
          prosjekt. Bilaget gaar til innboksen som ellers, men med prosjektet
          paa seg — det er grunnlaget for viderefaktureringen. */}
      {showProject && <button className="cam-pop-btn ghost" disabled={busy} onClick={() => onPick('prosjekt')}>{t('project.cost.toProject')}</button>}
      <button className="cam-pop-btn ghost" disabled={busy} onClick={() => onPick('innboks')}>{t('capture.toInbox')}</button>
      <button className="cam-pop-btn solid" disabled={busy} onClick={() => onPick('nyttkjop')}><Icon name="receipt" size={17} />{t('capture.toPurchase')}</button>
    </div>
  </React.Fragment>;
}
function CamCaptureButton({ setRoute, className, align }) {
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [pendingTarget, setPendingTarget] = useState(null);
  // Opplastinger som gaar i bakgrunnen. Knappen skal vise at noe skjer, men
  // aldri sperre for neste bilde.
  const [uploading, setUploading] = useState(0);
  const targetRef = useRef('innboks');
  const projectRef = useRef(null);
  const [projectPickOpen, setProjectPickOpen] = useState(false);
  // Leses uten hook: modulen lastes som eget script, og et betinget hook-kall
  // ville endret hook-rekkefolgen om det scriptet ikke var kjort enna.
  const projects = window.activeProjectsFromState ? window.activeProjectsFromState(window.PlummeBackend?.appState) : [];
  const paymentDecisionRef = useRef(null); const inputRef = useRef(null);
  const analyzeUploadedReceipt = (entryId) => {
    if (!entryId || !window.PlummeBackend?.analyzeReceiptInboxEntry) return;
    try {
      const analysis = window.PlummeBackend.analyzeReceiptInboxEntry(entryId);
      const timeout = new Promise((_, reject) => window.setTimeout(() => reject(new Error(t('capture.analysisTimedOut'))), 30000));
      Promise.race([analysis, timeout]).catch((err) => toast?.(err?.message || t('capture.analysisFailed'), 'warning'));
    } catch (err) {
      toast?.(err?.message || t('capture.analysisFailed'), 'warning');
    }
  };
  // Først velges mål og eventuell betalingskilde. Deretter åpnes mobilkameraet,
  // og fila lastes opp uten et nytt dialogsteg.
  const pick = async (target, { keepProject = false } = {}) => {
    targetRef.current = target; setOpen(false);
    if (!keepProject) projectRef.current = null;
    if (target === 'nyttkjop') { openCamera(null); return; }
    // Prosjektet velges foer betalingskilden. Bilaget skal uansett til
    // innboksen, sa resten av flyten er den samme.
    if (target === 'prosjekt') { setProjectPickOpen(true); return; }
    setBusy(true);
    try {
      const prepared = await window.PlummeCameraCaptureRoute.preparePaymentDialog(target);
      if (!prepared.ready) { toast?.(prepared.error?.message || t('capture.paymentDialogLoadFailed'), 'error'); return; }
      setPendingTarget(target);
    } catch (err) {
      toast?.(err?.message || t('capture.paymentDialogLoadFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  const openCamera = (paymentSourceDecision) => {
    paymentDecisionRef.current = paymentSourceDecision;
    setPendingTarget(null);
    window.setTimeout(() => inputRef.current?.click(), 0);
  };
  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (file) void upload([{ file, paymentSourceDecision: paymentDecisionRef.current }]);
  };
  // Bilag til innboksen skal ikke holde kameraet. Skal du ta ti kvitteringer paa
  // rad, sto du og ventet paa at forrige opplasting ga knappen tilbake. Na gaar
  // den i bakgrunnen: innboksen aapnes og «lastes opp» kommer med en gang, mens
  // «lastet opp» kommer naar bilaget faktisk ligger der. Analysen har aldri
  // blokkert den meldingen. Nytt kjop blir staaende og venter — det skjemaet
  // skal aapnes med bilaget i seg.
  const upload = async (prepared) => {
    const file = prepared?.[0]?.file;
    const paymentSourceDecision = prepared?.[0]?.paymentSourceDecision;
    if (!file) return;
    const toPurchase = targetRef.current === 'nyttkjop';
    if (toPurchase && busy) return;
    if (toPurchase) setBusy(true);
    else { setRoute('innboks'); toast?.(t('capture.uploadingToInbox'), 'info'); setUploading((count) => count + 1); }
    try {
      const project = projectRef.current;
      const saved = await window.PlummeBackend.uploadReceiptFileToInbox(file, {
        source: project ? 'home-camera-project' : toPurchase ? 'home-camera-purchase' : 'home-camera',
        paymentSourceDecision,
        projectId: project?.projectId,
        projectBillable: project?.projectBillable,
      });
      if (toPurchase) {
        window.__plummeRouteReceipt = saved;
        setRoute('nyttkjop');
        // The paid-purchase page picks up the voucher when it mounts. If the
        // route is already open, the event attaches it to the mounted form.
        window.dispatchEvent(new CustomEvent('plumme-route-receipt', { detail: saved }));
      }
      toast?.(toPurchase ? t('capture.readyInPurchase') : t('capture.uploadedToInbox'), 'success');
      analyzeUploadedReceipt(saved.id);
    } catch (err) {
      toast?.(err?.message || t('capture.uploadFailed'), 'error');
    } finally {
      if (toPurchase) { setBusy(false); paymentDecisionRef.current = null; }
      else setUploading((count) => Math.max(0, count - 1));
      projectRef.current = null;
    }
  };
  if (!window.PlummeBackend?.uploadReceiptFileToInbox) return null;
  return <div className={'cam-wrap' + (className ? ' ' + className : '')}>
    <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={chooseFile} />
    <button type="button" className="cam-btn" disabled={busy} onClick={() => setOpen((o) => !o)}
      aria-label={t('shell.camCapture')} title={uploading ? t('capture.uploadingToInbox') : t('shell.camCapture')}
      data-help-title={t('shell.camCapture')} data-help={t('shell.camHelp')}><Icon name={busy || uploading ? 'sparkle' : 'camera'} size={19} /></button>
    {open && <CamCapturePop onPick={pick} onClose={() => setOpen(false)} align={align} busy={busy} showProject={projects.length > 0 && !!window.ProjectCostDialog} />}
    {projectPickOpen && window.ProjectCostDialog && <window.ProjectCostDialog onClose={() => setProjectPickOpen(false)}
      onPick={(project) => { projectRef.current = project; setProjectPickOpen(false); void pick('innboks', { keepProject: true }); }} />}
    {pendingTarget && <ReceiptSinglePaymentSourceDialog busy={busy}
      onClose={() => setPendingTarget(null)} onConfirm={openCamera}/>}
  </div>;
}
function BottomNav({ route, setRoute, aa = true, vatRegistered = true }) {
  const [open, setOpen] = useState(null);
  const [hiddenPages, setHiddenPagesState] = useState(() => getHiddenPages());
  useEffect(() => { const sync = () => { const next = getHiddenPages(); setHiddenPagesState(next); setOpen((key) => window.PlummeMenuPreferences.filterNavigation(BOTTOM_NAV, next, PAGE_LOCKED, PAGE_RULE, [...(!aa ? ['arsregnskap'] : []), ...(!vatRegistered ? ['innsendinger'] : [])]).some((group) => group.key === key) ? key : null); }; window.addEventListener('plumme-pages-changed', sync); return () => window.removeEventListener('plumme-pages-changed', sync); }, [aa, vatRegistered]);
  const visibleNavigation = window.PlummeMenuPreferences.filterNavigation(BOTTOM_NAV, hiddenPages, PAGE_LOCKED, PAGE_RULE, [...(!aa ? ['arsregnskap'] : []), ...(!vatRegistered ? ['innsendinger'] : [])]);
  const wrapRef = useRef(null);
  const tabRefs = useRef({});
  const [popLeft, setPopLeft] = useState(0);
  const supportUnread = useSupportUnreadCount();
  const POP_W = 244;
  const inNav = (g) => {const activeRoute=route==='transaksjon'?'allebilag':route;return g.route===activeRoute||(g.items&&g.items.some((i)=>i.key===activeRoute))||(g.groups&&g.groups.some((gr)=>gr.items.some((i)=>i.key===activeRoute)));};
  const activeGroup = visibleNavigation.find(inNav);
  useEffect(() => {
    if (!open) return;
    const wrap = wrapRef.current, tab = tabRefs.current[open];
    if (!wrap || !tab) return;
    const wr = wrap.getBoundingClientRect(), tr = tab.getBoundingClientRect();
    const center = tr.left - wr.left + tr.width / 2;
    setPopLeft(Math.max(12, Math.min(center - POP_W / 2, wr.width - POP_W - 12)));
  }, [open]);
  const tap = (g) => { if (g.route) { setRoute(g.route); setOpen(null); } else setOpen((o) => o === g.key ? null : g.key); };
  const openGroup = visibleNavigation.find((g) => g.key === open);
  return <React.Fragment>
    {open && <div className="bnav-scrim" onClick={() => setOpen(null)} />}
    <CamCaptureButton setRoute={setRoute} className="cam-wrap-mobile" align="center" />
    <div className="bnav-wrap" ref={wrapRef}>
      {openGroup && <div className="bnav-pop" style={{ left: popLeft, width: POP_W }}>
        {openGroup.groups
          ? openGroup.groups.map((grp) => <div key={grp.label} className="bnav-pop-group">
              <div className="bnav-pop-label">{bottomLabel(grp)}</div>
              {grp.items.filter((it)=>aa||it.key!=='arsregnskap').map((it) => <a key={it.key} className={'bnav-pop-item' + (route === it.key ? ' active' : '')} href={navHref(it.key)} style={it.color ? { color: it.color } : null} onClick={navClick(() => { setRoute(it.key); setOpen(null); })}><span className="sb-nav-ico"><Icon name={it.icon} size={18} />{it.key==='support'&&supportUnread>0&&<Badge variant="error" dot/>}</span><span>{navLabel(it)}</span></a>)}
            </div>)
          : openGroup.items.filter((it)=>aa||it.key!=='arsregnskap').map((it) =>
            <a key={it.key} className={'bnav-pop-item' + (route === it.key ? ' active' : '')} href={navHref(it.key)} style={it.color ? { color: it.color } : null} onClick={navClick(() => { setRoute(it.key); setOpen(null); })}><span className="sb-nav-ico"><Icon name={it.icon} size={18} />{it.key==='support'&&supportUnread>0&&<Badge variant="error" dot/>}</span><span>{navLabel(it)}</span></a>)}
      </div>}
      <nav className="bnav">
        {visibleNavigation.map((g) => {
          const cls = 'bnav-tab' + ((open === g.key || (!open && activeGroup && activeGroup.key === g.key)) ? ' active' : '');
          const setTabRef = (el) => { tabRefs.current[g.key] = el; };
          const groupHasSupportUnread = g.key === 'annet' && supportUnread > 0;
          const body = <React.Fragment><span className="sb-nav-ico"><Icon name={g.icon} size={18} />{groupHasSupportUnread&&<Badge variant="error" dot/>}</span><span>{bottomLabel(g)}</span></React.Fragment>;
          // Gruppefanene apner en popover og er ingen side — bare faner med egen rute blir lenker.
          return g.route
            ? <a key={g.key} ref={setTabRef} className={cls} href={navHref(g.route)} onClick={navClick(() => tap(g))}>{body}</a>
            : <button key={g.key} ref={setTabRef} className={cls} onClick={() => tap(g)}>{body}</button>;
        })}
      </nav>
    </div>
  </React.Fragment>;
}

function ViewportBadge() {
  const [d, setD] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => { const f = () => setD({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return <div className="vp-badge">{d.w} × {d.h}</div>;
}
function Shell({ route, setRoute, year, setYear, collapsed, setCollapsed, trial, trialInfo, setTrial, onUpgradeTrial, client, setClient, children, lonnPage, setLonnPage, transactionPage, setTransactionPage, supportPage, setSupportPage }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : { user: null }; const aa=backend.appState?window.PlummeAccountingScope?.annualAccountsWorkflowAvailable?.(backend.appState.settings||{},year)!==false:true;
  const employeePortals = useEmployeePortalSwitcherRows(backend.user?.uid); const vatRegistered = backend.appState?.settings?.vatRegistered !== false;
  useEffect(() => { setMobileOpen(false); if(route==='arsregnskap'&&!aa)setRoute('arsavslutning'); if(route==='innsendinger'&&!vatRegistered)setRoute('hjem'); }, [route,aa,vatRegistered]);
  const clients = getClients();
  const clientName = (clients.find((c) => c.id === client) || clients[0] || CLIENTS[0] || { name: t('shell.client.none') }).name;
  return <div className="app">
    <ViewportBadge />
    {window.HelpLayer && <window.HelpLayer />}
    {window.HelpArticleLayer && <window.HelpArticleLayer />}
    <Sidebar route={route} setRoute={setRoute} collapsed={collapsed} setCollapsed={setCollapsed}
      mobileOpen={mobileOpen} closeMobile={() => setMobileOpen(false)} client={client} setClient={setClient} lonnPage={lonnPage} setLonnPage={setLonnPage} transactionPage={transactionPage} setTransactionPage={setTransactionPage} supportPage={supportPage} setSupportPage={setSupportPage} employeePortals={employeePortals} aa={aa} vatRegistered={vatRegistered} />
    {mobileOpen && <div className="sidebar-scrim" onClick={() => setMobileOpen(false)} />}{window.SupportFAB && <window.SupportFAB client={client} route={route} />}
    <main className="main" style={{ color: "var(--ink-2)" }}>
      <div className="main-inner">
        <Topbar year={year} setYear={setYear} onMenu={() => setMobileOpen(true)} clientName={clientName} route={route} client={client} setClient={setClient} setRoute={setRoute} />
        {trial && <TrialBanner info={trialInfo} onUpgrade={onUpgradeTrial} onClose={() => setTrial(false)} />}
        <window.AccountingJournalRecoveryGate route={route} setRoute={setRoute}>{children}</window.AccountingJournalRecoveryGate>
      </div>
    </main>
    <BottomNav route={route} setRoute={setRoute} aa={aa} vatRegistered={vatRegistered} />
  </div>;
}
Object.assign(window, { NAV, CLIENTS, getClients, employeePortalKey, rememberEmployeePortal, getRememberedEmployeePortal, ClientSwitcher, Sidebar, Topbar, TrialBanner, BottomNav, Shell });
